import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  ReadonlyValue,
  RowMenu,
  SearchBox,
  SkeletonTable,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import type { Currency } from './api'
import { createCurrency, fetchCurrencies, updateCurrency } from './api'

/**
 * Справочник валют (тикет #97, макет AdmCurrencies.dc.html).
 *
 * Код валюты на существующей строке не редактируется: на него ссылаются счета, балансы и
 * курсы. Удаления нет по той же причине — в меню строки его нет вовсе, а не «недоступно».
 * Ошибочный код исправляется новой валютой.
 *
 * Курсов ЦБ, признака основной валюты и их загрузки в справочнике нет — колонки макета
 * «Основная» и «Курс ЦБ» не показаны, а не заполнены выдуманным.
 */
const CODE = /^[A-Z]{3}$/
const NUMERIC = /^\d{3}$/
const MINOR_UNITS = [0, 2, 3]

export function CurrenciesPage() {
  const { t, locale } = useApp()
  const editing = useAdminEditing()
  const currencies = useQuery({ queryKey: ['admin-currencies'], queryFn: ({ signal }) => fetchCurrencies(signal) })
  const [draft, setDraft] = useState<CurrencyDraft | null>(null)
  const [query, setQuery] = useState('')

  const list = currencies.data ?? []
  const needle = query.trim().toLowerCase()
  const shown = list.filter((currency) =>
    !needle || [currency.code, currency.numericCode, currency.name].some((value) => value.toLowerCase().includes(needle)),
  )
  const forbidden = isForbidden(currencies.error)
  const startNew = (code = '') => setDraft({ isNew: true, code, numericCode: '', name: '', symbol: '', minorUnits: '2' })

  return (
    <AdminPage
      titleKey="nav.adminCurrencies"
      count={currencies.data ? (needle ? t('adm.ofTotal', { shown: shown.length, total: list.length }) : list.length.toLocaleString(locale)) : undefined}
      leadKey="adm.currency.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.currency.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && <PrimaryButton label={t('admin.currency.add')} onClick={() => startNew()} />}
    >
      {!forbidden && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('adm.currency.search')} />
        </div>
      )}
      <AdminState
        isLoading={currencies.isLoading}
        error={currencies.error}
        onRetry={() => void currencies.refetch()}
        forbiddenTitleKey="adm.currency.forbidden"
        errorTitleKey="adm.currency.error"
        skeleton={<SkeletonTable columns={5} rows={6} />}
      >
        {list.length === 0 ? (
          <StateView boxed kind="setup" title={t('adm.currency.setup')} description={t('adm.currency.setup.text')} />
        ) : shown.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.currency.empty', { query })}
                     actions={
                       <>
                         <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>{t('adm.resetSearch')}</button>
                         {editing.mayEdit && /^[a-z]{3}$/i.test(query.trim()) && (
                           <button type="button" className="btn btn-ghost btn-sm" onClick={() => startNew(query.trim().toUpperCase())}>
                             <Icon name="plus" size="sm" />{t('adm.currency.addCode', { code: query.trim().toUpperCase() })}
                           </button>
                         )}
                       </>
                     } />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('nav.adminCurrencies')}>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>{t('adm.currency.code')}</th>
                  <th>{t('admin.currency.numericCode')}</th>
                  <th>{t('admin.currency.name')}</th>
                  <th>{t('admin.currency.symbol')}</th>
                  <th className="num">{t('admin.currency.minorUnits')}</th>
                  {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((currency) => (
                  <tr key={currency.code} className={draft && !draft.isNew && draft.code === currency.code ? 'is-selected' : undefined}>
                    <td><span className="mono strong">{currency.code}</span></td>
                    <td><span className="mono muted">{currency.numericCode}</span></td>
                    <td>{currency.name}</td>
                    <td>{currency.symbol ?? <span className="muted">—</span>}</td>
                    <td className="num">{currency.minorUnits}</td>
                    {editing.mayEdit && (
                      <td className="adm-act">
                        <RowMenu label={t('adm.currency.actions', { code: currency.code })} items={[
                          {
                            id: 'edit',
                            label: t('admin.edit'),
                            icon: 'adm-edit',
                            onSelect: () => setDraft({
                              isNew: false,
                              code: currency.code,
                              numericCode: currency.numericCode,
                              name: currency.name,
                              symbol: currency.symbol ?? '',
                              minorUnits: String(currency.minorUnits),
                            }),
                          },
                        ]} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminState>
      {draft && <CurrencySheet draft={draft} onChange={setDraft} onClose={() => setDraft(null)} />}
    </AdminPage>
  )
}

interface CurrencyDraft {
  isNew: boolean
  code: string
  numericCode: string
  name: string
  symbol: string
  minorUnits: string
}

function CurrencySheet({ draft, onChange, onClose }: {
  draft: CurrencyDraft
  onChange: (draft: CurrencyDraft) => void
  onClose: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const code = draft.code.trim().toUpperCase()
  const codeError = draft.isNew && draft.code !== '' && !CODE.test(code) ? t('adm.currency.codeInvalid', { count: code.length }) : null
  const numericError = draft.isNew && draft.numericCode !== '' && !NUMERIC.test(draft.numericCode.trim()) ? t('adm.currency.numericInvalid') : null
  const incomplete = !CODE.test(code) || !NUMERIC.test(draft.numericCode.trim()) || draft.name.trim() === ''

  const save = useMutation({
    mutationFn: () => {
      const body: Currency = {
        code,
        numericCode: draft.numericCode.trim(),
        name: draft.name,
        symbol: draft.symbol || null,
        minorUnits: Number(draft.minorUnits || '2'),
      }
      return draft.isNew ? createCurrency(body) : updateCurrency(body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-currencies'] })
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  const units = MINOR_UNITS.includes(Number(draft.minorUnits)) ? MINOR_UNITS : [...MINOR_UNITS, Number(draft.minorUnits)].sort()

  return (
    <EditLayer
      kind="sheet"
      overline={t('admin.currency.form')}
      title={draft.isNew ? t('adm.currency.new') : `${draft.code} — ${draft.name}`}
      onClose={onClose}
      hint={draft.isNew && incomplete ? t('adm.currency.fillCode') : undefined}
      footer={<LayerButtons onCancel={onClose} saveLabel={draft.isNew ? t('admin.currency.add') : t('admin.save')}
                            pending={save.isPending} disabled={incomplete} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.currency.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          {draft.isNew ? (
            <>
              <Field label={t('adm.currency.code')} htmlFor="cur-code" required error={codeError} help={t('adm.currency.code.help')}>
                <input id="cur-code" className={codeError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(codeError)}
                       maxLength={3} value={draft.code} onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })} />
              </Field>
              <Field label={t('admin.currency.numericCode')} htmlFor="cur-num" required error={numericError} help={t('adm.currency.numeric.help')}>
                <input id="cur-num" className={numericError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(numericError)}
                       maxLength={3} inputMode="numeric" value={draft.numericCode}
                       onChange={(e) => onChange({ ...draft, numericCode: e.target.value })} />
              </Field>
            </>
          ) : (
            <>
              <Field label={t('adm.currency.code')} help={t('adm.currency.codeLocked')}>
                <ReadonlyValue mono>{draft.code}</ReadonlyValue>
              </Field>
              <Field label={t('admin.currency.numericCode')} help={t('adm.currency.numericLocked')}>
                <ReadonlyValue mono>{draft.numericCode}</ReadonlyValue>
              </Field>
            </>
          )}
        </div>
        {draft.isNew && (
          <div className="alert alert-info">
            <Icon name="adm-info" />
            <span>{t('adm.currency.newNote')}</span>
          </div>
        )}
        <Field label={t('admin.currency.name')} htmlFor="cur-name" required>
          <input id="cur-name" className="input" value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
        </Field>
        <div className="form-grid">
          <Field label={t('admin.currency.symbol')} htmlFor="cur-sym" help={t('adm.currency.symbol.help')}>
            <input id="cur-sym" className="input" maxLength={8} value={draft.symbol} onChange={(e) => onChange({ ...draft, symbol: e.target.value })} />
          </Field>
          <Field label={t('admin.currency.minorUnits')}>
            <div className="seg" role="group" aria-label={t('admin.currency.minorUnits')} style={{ width: 'fit-content' }}>
              {units.map((value) => (
                <button key={value} type="button" aria-pressed={Number(draft.minorUnits) === value}
                        onClick={() => onChange({ ...draft, minorUnits: String(value) })}>
                  {value}
                </button>
              ))}
            </div>
          </Field>
        </div>
        {!draft.isNew && (
          <div className="alert alert-neutral">
            <Icon name="adm-info" />
            <span>{t('adm.currency.editNote')}</span>
          </div>
        )}
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
